// SCR-PUBLIC-013 — تأیید اصالت گواهی با QR (/cv/:code؛ US-DOC-052؛ AC-DOC-052.1؛ C-AGENT-1488): فقط خلاصه غیرحساس (نوع، شماره، تاریخ صدور، وضعیت
// معتبر/باطل، بازه استراحت، نام و کد نظام ارائه‌دهنده و نام مرکز) بدون نام یا شناسه مراجعه‌کننده و متن گواهی. کد نامعتبر ← پیام عمومی.
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { isApiError, unwrap } from '@phoenix/api-client';
import { usePublicApi } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, JalaliDate, LoadingState } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useParams } from 'react-router';

export const SCR_PUBLIC_013 = 'SCR-PUBLIC-013';

export function PublicCertificateVerifyPage() {
  const { t } = useTranslation('doc');
  const api = usePublicApi();
  const { code = '' } = useParams();
  const query = useQuery({
    queryKey: ['documents-public-certificate', code],
    queryFn: () => unwrap(api.GET('/api/v1/documents/public/certificates/{code}', { params: { path: { code } } })),
    retry: false,
  });
  useEffect(() => {
    document.title = `${t('doc:certificate_verify.title')} · ${t('shell:app.staff_name')}`;
  }, [t]);
  const data = query.data;
  const valid = data?.status === 'Issued';
  return (
    <Box data-screen-id={SCR_PUBLIC_013} sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 1.5, sm: 3 } }}>
      <Paper variant="outlined" component="main" sx={{ p: { xs: 2, sm: 4 }, maxWidth: 640, mx: 'auto' }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
          <VerifiedOutlinedIcon color="primary" />
          <Typography variant="h1">{t('doc:certificate_verify.title')}</Typography>
        </Stack>
        {query.isPending ? (
          <LoadingState rows={3} />
        ) : query.error ? (
          isApiError(query.error) && query.error.status === 404 ? (
            <Alert severity="warning" data-testid="doc-certificate-verify-invalid">
              {t('doc:certificate_verify.invalid')}
            </Alert>
          ) : (
            <ErrorState error={query.error} onRetry={() => void query.refetch()} />
          )
        ) : data ? (
          <Stack spacing={1} data-testid="doc-certificate-verify-result">
            <Alert severity={valid ? 'success' : 'error'} data-testid="doc-certificate-verify-status" data-status={data.status}>
              {t(`doc:certificate_verify.status.${data.status}`, { defaultValue: data.status })}
            </Alert>
            <Typography>
              {t(`doc:certificate.kinds.${data.kind}`, { defaultValue: data.kind })}: <bdi dir="ltr">{data.number}</bdi>
            </Typography>
            <Typography>
              {t('doc:certificate_verify.issued_on')}: <JalaliDate value={data.issuedOn} />
            </Typography>
            {data.restFrom && data.restTo && (
              <Typography>
                {t('doc:certificate_verify.rest')}: <JalaliDate value={data.restFrom} /> – <JalaliDate value={data.restTo} />
              </Typography>
            )}
            {data.providerName && (
              <Typography>
                {t('doc:certificate_verify.provider')}: {data.providerName}
                {data.councilNumber ? ` · ${t('doc:certificate_verify.council', { number: faDigits(data.councilNumber) })}` : ''}
              </Typography>
            )}
            {data.centerName && (
              <Typography>
                {t('doc:certificate_verify.center')}: {data.centerName}
              </Typography>
            )}
          </Stack>
        ) : null}
      </Paper>
    </Box>
  );
}
